import { useQuery } from '@tanstack/react-query';
import { http } from 'services/http';
import { CLIENTS_GET_MANY, CLIENTS_SEARCH_HINTS } from 'api/query-keys.ts';
import type { ClientRes, ClientType } from 'api/swagger';
import { PAGINATION_DEFAULT_LIMIT } from 'shared/constants/paginations';
import type { WithPagination } from 'shared/utils/pagination.ts';
import { preparePagination } from 'shared/utils/pagination.ts';
import type { IWithPagination } from 'types/api-query.ts';

interface IParams extends IWithPagination {
  search?: string;
  isHints?: boolean;
  clientTypes?: ClientType[];
  enabled?: boolean;
}

export const useGetManyClients = (params?: IParams) => {
  const { limit = PAGINATION_DEFAULT_LIMIT, page = 1, search, isHints = false, enabled, clientTypes } = params || {};

  const qs = new URLSearchParams({ limit: String(limit), page: String(page) });

  if (search) {
    qs.set('filter[search]', search);
  }
  if (clientTypes) {
    clientTypes.forEach((clientType, index) => {
      qs.set(`filter[clientTypes][${index}]`, clientType);
    });
  }

  const queryString = qs.toString();

  let queryKey;
  if (isHints) {
    queryKey = [CLIENTS_SEARCH_HINTS, queryString];
  } else {
    queryKey = [CLIENTS_GET_MANY, queryString];
  }

  return useQuery<WithPagination<ClientRes[]>>({
    queryKey,
    queryFn: async () => {
      const { data, headers } = await http.get<ClientRes[]>(`/v1/clients?${queryString}`);
      const pagination = preparePagination(headers);

      return { data, pagination };
    },
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 15 * 1000,
    cacheTime: 60 * 1000,
    enabled,
  });
};
